import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { api, ApiError } from "../data/api.js";
import { useIdentity } from "../data/identity.js";

// Přihlášení e-mailovým odkazem (REQ-032): formulář v Moje já a výzva „Ulož si výsledek“ na Skice
// majitele (REQ-033, Libor 19. 10. 2026). Po kliknutí na odkaz se k identitě připojí všechna
// sezení z tohoto zařízení.

type SendState = { kind: "idle" } | { kind: "sending" } | { kind: "sent" } | { kind: "dev"; link: string } | { kind: "failed" } | { kind: "limited" };

export function LoginForm({ next, title = null, lead = "me.lead", testId = "login-form" }: { next: string | null; title?: string | null; lead?: string; testId?: string }) {
  const intl = useIntl();
  const lang = intl.locale;
  const [email, setEmail] = useState("");
  const [state, setState] = useState<SendState>({ kind: "idle" });

  async function send(e: FormEvent) {
    e.preventDefault();
    setState({ kind: "sending" });
    try {
      const r = await api.identityLogin(email, lang, next ?? `/${lang}/me`);
      setState(r.dev_link !== undefined ? { kind: "dev", link: r.dev_link } : { kind: "sent" });
    } catch (err) {
      setState(err instanceof ApiError && err.status === 429 ? { kind: "limited" } : { kind: "failed" });
    }
  }

  return (
    <form className="card stack" onSubmit={(e) => void send(e)} data-testid={testId}>
      {title === null ? null : (
        <h2 className="h-small">
          <FormattedMessage id={title} />
        </h2>
      )}
      <p>
        <FormattedMessage id={lead} />
      </p>
      <label className="field">
        <span className="label">
          <FormattedMessage id="me.email.label" />
        </span>
        <input className="share-url" type="email" name="email" required autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
      </label>
      <button type="submit" className="button" disabled={state.kind === "sending"}>
        <FormattedMessage id="me.email.send" />
      </button>
      {state.kind === "sent" ? (
        <p className="notice small" role="status" data-testid="login-sent">
          <FormattedMessage id="me.email.sent" />
        </p>
      ) : state.kind === "dev" ? (
        <p className="notice small" role="status">
          <FormattedMessage id="me.email.devLink" />{" "}
          <a href={state.link} data-testid="login-dev-link">
            {state.link}
          </a>
        </p>
      ) : state.kind === "failed" || state.kind === "limited" ? (
        <p className="small" role="alert">
          <FormattedMessage id={state.kind === "limited" ? "me.rateLimited" : "me.email.failed"} />
        </p>
      ) : null}
    </form>
  );
}

/** Výzva na Skice majitele, který není přihlášený (REQ-033): uloží výsledek do Moje já. */
export function SaveResult() {
  const identity = useIdentity();
  const { locale } = useIntl();
  if (identity.kind !== "anon") return null;
  return <LoginForm next={`/${locale}/me`} title="sketch.save.title" lead="sketch.save.lead" testId="save-result" />;
}

/**
 * Hned po dokončení testu, ještě před Skicou (REQ-033, Libor 19. 10. 2026): „Kam ti máme Skicu
 * uložit?“. Jen majiteli bez přihlášení a jen jednou po dokončení, „Teď ne“ ukáže Skicu.
 */
export function SaveFirst({ onSkip }: { onSkip: () => void }) {
  const { locale } = useIntl();
  return (
    <div className="stack save-first" data-testid="save-first">
      <h1>
        <FormattedMessage id="save.first.title" />
      </h1>
      <LoginForm next={`/${locale}/me`} lead="save.first.lead" testId="save-first-form" />
      <button type="button" className="button button--quiet" onClick={onSkip} data-testid="save-skip">
        <FormattedMessage id="save.first.skip" />
      </button>
    </div>
  );
}
